import {
    saveModSettings,
    type ModSetting,
    type ModSettingPreset,
    type ModSettingsPayload,
} from '../api/mods';
import { t } from '../i18n';

// ============================================================
// Окно «Исключения из рандомизации» для модов.
// Модератор/ghost/админ правит ОБЩИЕ исключения: они сохраняются на сервере
// (`game_mod_setting_params.disabled` / `excluded_values`) и действуют для всех.
// Обычный пользователь видит те же значения по умолчанию, может их изменить —
// правки ложатся в localStorage (только его браузер) и сбрасываются кнопкой
// «Сбросить на умолчания». Модератор при сохранении затирает и свои локальные
// правки, чтобы общие настройки снова были главными.
// ============================================================

export interface ModExclusionsSource {
    id: number;
    name: string;
    /** Серверные (общие) настройки мода. */
    settings: ModSetting | null;
    presets: ModSettingPreset[];
}

export interface ModExclusionsOptions {
    /** true — модератор, сохраняем в БД; false — локальные правки пользователя. */
    canEditShared: boolean;
    token?: string | null;
}

export interface ModExclusionsResult {
    /** Эффективные настройки, по которым нужно рандомизировать. */
    settings: ModSetting;
    /** true — сохранено на сервере как общие настройки. */
    shared: boolean;
}

/** Сводка исключений мода — для счётчика на кнопке и в шапке окна. */
export interface ModExclusionsCount {
    /** Параметры, выключенные целиком. */
    disabled: number;
    /** Исключённые отдельные значения (без выключенных параметров). */
    values: number;
    /** Всего исключений: параметры целиком + отдельные значения. */
    total: number;
}

/** Считает исключения по настройкам мода (для бейджа на кнопке). */
export function countModExclusions(setting: ModSetting | null | undefined): ModExclusionsCount {
    if (!setting) return { disabled: 0, values: 0, total: 0 };
    let disabled = 0;
    let values = 0;
    for (const param of setting.params) {
        if (param.disabled) {
            disabled += 1;
            continue;
        }
        values += (param.excluded_values ?? []).filter((v) => param.values.includes(v)).length;
    }
    return { disabled, values, total: disabled + values };
}

/** Локальная правка одного параметра (чего нет — берётся из общих настроек). */
interface LocalParam {
    disabled?: boolean;
    excluded?: string[];
}

type LocalMod = Record<string, LocalParam>;
type LocalAll = Record<string, LocalMod>;

const LOCAL_KEY = 'mods.exclusions.local';

function readLocalAll(): LocalAll {
    try {
        const raw = localStorage.getItem(LOCAL_KEY);
        if (!raw) return {};
        const data = JSON.parse(raw) as LocalAll;
        return data && typeof data === 'object' ? data : {};
    } catch {
        return {};
    }
}

function writeLocalAll(data: LocalAll): void {
    try {
        localStorage.setItem(LOCAL_KEY, JSON.stringify(data));
    } catch {
        /* localStorage недоступен — работаем без сохранения */
    }
}

/** Накладывает локальные исключения пользователя на общие настройки мода. */
export function applyLocalExclusions(modId: number, setting: ModSetting): ModSetting {
    const local = readLocalAll()[String(modId)];
    if (!local) return setting;

    const params = setting.params.map((param) => {
        const entry = local[param.name];
        if (!entry) return param;
        const excluded = (param.excluded_values ?? []).filter((v) => param.values.includes(v));
        // Локальная правка заменяет общее значение целиком (включая возврат
        // значения, исключённого модератором).
        const wanted = Array.isArray(entry.excluded) ? entry.excluded : null;
        const nextExcluded = wanted ? param.values.filter((v) => wanted.includes(v)) : excluded;
        return {
            ...param,
            disabled: typeof entry.disabled === 'boolean' ? entry.disabled : param.disabled,
            excluded_values: nextExcluded,
        };
    });

    return { ...setting, params };
}

/** Сохраняет локальные отличия черновика от общих настроек. */
function saveLocalExclusions(
    modId: number,
    base: ModSetting,
    drafts: Map<string, ExclusionsDraft>
): void {
    const local: LocalMod = {};

    for (const param of base.params) {
        const draft = drafts.get(param.name);
        if (!draft) continue;
        const baseDisabled = param.disabled === true;
        const baseExcluded = param.excluded_values ?? [];
        const excluded = param.values.filter((v) => draft.excluded.has(v));
        const same =
            draft.disabled === baseDisabled &&
            excluded.length === baseExcluded.length &&
            excluded.every((v) => baseExcluded.includes(v));
        if (!same) local[param.name] = { disabled: draft.disabled, excluded };
    }

    const all = readLocalAll();
    if (Object.keys(local).length > 0) all[String(modId)] = local;
    else delete all[String(modId)];
    writeLocalAll(all);
}

/** Убирает локальные правки мода (после сохранения общих настроек модератором). */
export function clearLocalExclusions(modId: number): void {
    const all = readLocalAll();
    if (!(String(modId) in all)) return;
    delete all[String(modId)];
    writeLocalAll(all);
}

/** Черновик исключений по имени параметра. */
interface ExclusionsDraft {
    disabled: boolean;
    excluded: Set<string>;
}

/** Открытое окно (одно за раз): закрывает предыдущее без сохранения. */
let active: { close: (result: ModExclusionsResult | null) => void } | null = null;

/**
 * Открывает окно исключений. Резолвится эффективными настройками после
 * сохранения или null, если окно закрыли без сохранения.
 */
export function openModExclusionsModal(
    mod: ModExclusionsSource,
    options: ModExclusionsOptions
): Promise<ModExclusionsResult | null> {
    // Повторное открытие: предыдущее окно закрываем, чтобы не плодить оверлеи.
    active?.close(null);

    const base = mod.settings;
    if (!base || base.params.length === 0) return Promise.resolve(null);

    const canEditShared = options.canEditShared;
    const token = options.token ?? null;

    const makeDrafts = (from: ModSetting): Map<string, ExclusionsDraft> => {
        const map = new Map<string, ExclusionsDraft>();
        for (const param of base.params) {
            const source = from.params.find((p) => p.name === param.name) ?? param;
            map.set(param.name, {
                disabled: source.disabled === true,
                excluded: new Set(source.excluded_values ?? []),
            });
        }
        return map;
    };

    // Модератор видит общие настройки, пользователь — их же с наложенными
    // своими локальными правками.
    let drafts = makeDrafts(canEditShared ? base : applyLocalExclusions(mod.id, base));

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay mod-exclusions-overlay';

    const modal = document.createElement('div');
    modal.className = 'modal mod-exclusions-modal';

    // --- Шапка ---
    const header = document.createElement('div');
    header.className = 'modal-header';

    const title = document.createElement('h3');
    title.textContent = t('mods.exclusions_title');
    header.appendChild(title);

    const headerActions = document.createElement('div');
    headerActions.className = 'modal-header-actions';
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'modal-close';
    closeBtn.textContent = '✕';
    closeBtn.title = t('common.close');
    headerActions.appendChild(closeBtn);
    header.appendChild(headerActions);
    modal.appendChild(header);

    // --- Тело ---
    const body = document.createElement('div');
    body.className = 'mod-exclusions-body';

    const hint = document.createElement('p');
    hint.className = 'hint';
    hint.textContent = canEditShared
        ? t('mods.exclusions_hint', { name: mod.name })
        : t('mods.exclusions_hint_local', { name: mod.name });
    body.appendChild(hint);

    // Сводка: сколько исключено сейчас — чтобы не выискивать по списку
    const summary = document.createElement('div');
    summary.className = 'mod-exclusions-summary';
    const sumParams = document.createElement('span');
    const sumValues = document.createElement('span');
    summary.append(sumParams, sumValues);
    body.appendChild(summary);

    /** Пересчитывает сводку по текущему черновику. */
    const renderSummary = (): void => {
        let disabled = 0;
        let values = 0;
        let total = 0;
        for (const param of base.params) {
            const draft = drafts.get(param.name);
            total += param.values.length;
            if (!draft) continue;
            if (draft.disabled) disabled += 1;
            else values += draft.excluded.size;
        }
        summary.classList.toggle('is-empty', disabled === 0 && values === 0);
        sumParams.textContent = t('mods.exclusions_summary_params', { n: disabled });
        sumValues.textContent = t('mods.exclusions_summary_values', { n: values, total });
    };

    const list = document.createElement('div');
    list.className = 'mod-exclusions-list';
    body.appendChild(list);

    const actions = document.createElement('div');
    actions.className = 'form-actions mod-exclusions-actions';

    const status = document.createElement('span');
    status.className = 'mod-exclusions-status';

    const resetBtn = document.createElement('button');
    resetBtn.type = 'button';
    resetBtn.className = 'btn-secondary';
    resetBtn.textContent = t('mods.exclusions_reset');

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn-secondary';
    cancelBtn.textContent = t('common.cancel');

    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'btn-primary';
    saveBtn.textContent = t('common.save');

    actions.append(resetBtn, status, cancelBtn, saveBtn);
    body.appendChild(actions);
    modal.appendChild(body);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // --- Отрисовка списка параметров ---
    const renderList = (): void => {
        list.innerHTML = '';

        for (const param of base.params) {
            const draft = drafts.get(param.name)!;

            const item = document.createElement('div');
            item.className = 'mod-exclusions-item' + (draft.disabled ? ' is-off' : '');

            const head = document.createElement('label');
            head.className = 'mod-exclusions-param';
            head.title = t('mods.exclusions_disable_hint');

            const check = document.createElement('input');
            check.type = 'checkbox';
            check.className = 'mod-exclusions-check';
            check.checked = draft.disabled;
            check.addEventListener('change', () => {
                draft.disabled = check.checked;
                renderList();
            });

            const name = document.createElement('span');
            name.className = 'mod-exclusions-name';
            name.textContent = (param.depends_on ? '↳ ' : '') + param.name;

            head.append(check, name);
            item.appendChild(head);

            const count = document.createElement('span');
            count.className = 'mod-exclusions-count';
            count.textContent = draft.disabled
                ? t('mods.exclusions_disabled')
                : t('mods.exclusions_excluded', {
                      n: draft.excluded.size,
                      total: param.values.length,
                  });
            item.appendChild(count);

            if (param.values.length > 0) {
                const values = document.createElement('div');
                values.className = 'mod-exclusions-values';

                for (const value of param.values) {
                    const chip = document.createElement('button');
                    chip.type = 'button';
                    const excluded = draft.excluded.has(value);
                    chip.className = 'mod-excl-chip' + (excluded ? ' is-excluded' : '');
                    chip.textContent = value;
                    chip.title = excluded
                        ? t('mods.exclusions_value_back_hint')
                        : t('mods.exclusions_value_hint');
                    chip.addEventListener('click', () => {
                        // Последнее значение исключать нельзя — рандомизатору нужен выбор.
                        const allowed = param.values.filter((v) => !drafts.get(param.name)!.excluded.has(v));
                        if (!excluded && allowed.length <= 1) {
                            status.textContent = t('mods.exclusions_last_value');
                            status.classList.add('is-error');
                            return;
                        }
                        status.textContent = '';
                        status.classList.remove('is-error');
                        const current = drafts.get(param.name)!;
                        if (excluded) current.excluded.delete(value);
                        else current.excluded.add(value);
                        renderList();
                    });
                    values.appendChild(chip);
                }

                item.appendChild(values);
            }

            list.appendChild(item);
        }

        renderSummary();
    };

    // --- Закрытие/сохранение ---
    let resolve!: (result: ModExclusionsResult | null) => void;
    const promise = new Promise<ModExclusionsResult | null>((res) => {
        resolve = res;
    });

    const close = (result: ModExclusionsResult | null): void => {
        document.removeEventListener('keydown', onKeydown, true);
        overlay.remove();
        if (active?.close === close) active = null;
        resolve(result);
    };

    active = { close };

    const onKeydown = (e: KeyboardEvent): void => {
        if (e.key !== 'Escape') return;
        e.stopImmediatePropagation();
        e.preventDefault();
        close(null);
    };
    document.addEventListener('keydown', onKeydown, true);

    closeBtn.addEventListener('click', () => close(null));
    cancelBtn.addEventListener('click', () => close(null));

    // Для модератора — возврат к сохранённым общим настройкам,
    // для пользователя — к общим настройкам модератора (сброс локальных правок).
    resetBtn.addEventListener('click', () => {
        drafts = makeDrafts(base);
        status.textContent = '';
        status.classList.remove('is-error');
        renderList();
    });

    saveBtn.addEventListener('click', () => {
        // Локальный режим: сохраняем отличия от общих настроек в браузере.
        if (!canEditShared) {
            saveLocalExclusions(mod.id, base, drafts);
            close({ settings: applyLocalExclusions(mod.id, base), shared: false });
            return;
        }

        const payload: ModSettingsPayload = {
            params: base.params.map((param) => {
                const draft = drafts.get(param.name)!;
                return {
                    name: param.name,
                    values: param.values,
                    randomize: param.randomize !== false,
                    default_value: param.default_value,
                    depends_on: param.depends_on,
                    depends_values: param.depends_values ?? [],
                    disabled: draft.disabled,
                    excluded_values: param.values.filter((v) => draft.excluded.has(v)),
                };
            }),
            // Пресеты не трогаем, но отправляем: PATCH заменяет набор настроек целиком.
            presets: mod.presets.map((p) => ({ name: p.name, values: { ...p.values } })),
        };

        saveBtn.disabled = true;
        resetBtn.disabled = true;
        status.textContent = t('common.saving');
        status.classList.remove('is-error');

        void saveModSettings(mod.id, payload, token)
            .then((res) => {
                // Общие настройки главнее локальных — свои правки убираем.
                clearLocalExclusions(mod.id);
                close({ settings: res.settings ?? base, shared: true });
            })
            .catch((err) => {
                console.error('[mod-exclusions] save error:', err);
                saveBtn.disabled = false;
                resetBtn.disabled = false;
                status.textContent = t('mods.exclusions_save_error');
                status.classList.add('is-error');
            });
    });

    renderList();
    return promise;
}
